<template>
  <div class="k-numeric-input" :class="{'ph-no-capture': posthogNoCapture}">
    <fieldset class="detail-form form-entry">
      <label v-if="label" :for="inputId" class="field-label">{{ label }}</label>
      <k-icon-button aria-label="increase-value"
                     icon="fas fa-angle-up"
                     :class="'increase-button'"
                     @on-click="increaseValue"
                     :disabled="readOnly"></k-icon-button>
      <k-icon-button aria-label="decrease-value"
                     icon="fas fa-angle-down"
                     :class="'decrease-button'"
                     @on-click="decreaseValue"
                     :disabled="readOnly"></k-icon-button>
      <k-icon-button v-if="!disableClearing"
                     aria-label="reset-value"
                     icon="fas fa-times"
                     :class="'reset-button'"
                     @on-click="resetInput"
                     :disabled="readOnly"></k-icon-button>
      <input :id="inputId" class="form-control"
             type="number"
             @input="updateNumber"
             :placeholder="inputPlaceholder"
             :readonly="readOnly"
             v-model="inputNumber"
      >
      <p v-if="useDefaultMessages" class="validation-message">{{ validationMessage }}</p>
    </fieldset>
  </div>
</template>

<style scoped>
.k-numeric-input {
  margin-bottom: 1em;
  position: relative;
}

.field-label {
  display: block;
  font-style: italic;
  color: var(--kate-type-link) !important;
}

.k-numeric-input button.reset-button {
  position: absolute;
  cursor: pointer;
  padding: 8px 8px 10px;
  right: 0;
}

.k-numeric-input button.reset-button:hover:not([disabled]) {
  color: var(--kate-type-danger);
}

.k-numeric-input button.increase-button {
  position: absolute;
  cursor: pointer;
  padding: 3px 8px 0;
  left: 0;
  top: 0;
}

.k-numeric-input button.decrease-button[disabled],
.k-numeric-input button.increase-button[disabled],
.k-numeric-input button[disabled] {
  color: var(--kate-disabled);
  cursor: unset;
}

.k-numeric-input button.decrease-button {
  position: absolute;
  cursor: pointer;
  padding: 0 8px;
  left: 0;
  bottom: 0;
}

.k-numeric-input .form-control[readonly] {
  background-color: var(--input-background);
  pointer-events: none;
}

.k-numeric-input .form-control {
  padding-left: 30px;
}

p.validation-message {
  color: var(--kate-type-danger);
  font-size: 0.85em;
  margin: 5px 0 0;
  position: absolute;
  white-space: nowrap;
}

/* Hide the up/down arrows on number inputs */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  /* stylelint-disable-next-line */
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  /* stylelint-disable-next-line */
  -moz-appearance: textfield;
}
</style>

<script>

import KIconButton from './buttons/k-icon-button.vue';

export default {
  components: { KIconButton },
  props: {
    modelValue: {
      type: [Number, String],
    },
    inputPlaceholder: {
      type: String,
      required: false,
      default: 'Enter value...',
    },
    inputId: {
      type: String,
      default: 'k-numeric-input',
    },
    label: {
      type: String,
      default: '',
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    step: {
      type: Number,
      default: 0.1,
    },
    min: {
      type: Number,
      default: -Infinity,
    },
    max: {
      type: Number,
      default: Infinity,
    },
    precision: {
      type: Number,
      default: 1, // Number of decimal places
    },
    useDefaultMessages: {
      type: Boolean,
      default: true,
    },
    posthogNoCapture: {
      type: Boolean,
      default: false,
    },
    disableClearing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputNumber: this.modelValue,
      validationMessage: '',
    };
  },
  watch: {
    modelValue() {
      this.inputNumber = this.modelValue;
    },
    inputNumber() {
      this.$emit('update:modelValue', this.inputNumber);
    },
  },

  computed: {
    nDecimalPlaces() {
      if (this.inputNumber.toString().includes('.')) {
        return this.inputNumber.toString().split('.')[1].length;
      }
      return 0;
    },
    tooLargeMessage() {
      return `Value must be less than or equal to ${this.max}`;
    },
    tooSmallMessage() {
      return `Value must be greater than or equal to ${this.min}`;
    },
    precisionMessage() {
      return `Value must have at most ${this.precision} decimal place${this.precision > 1 ? 's' : ''}`;
    },
  },

  methods: {
    trimDecimalPlaces(num) {
      if (num.toString().includes('.')) {
        return Math.floor(num.toFixed(this.precision + 1) * (10 ** this.precision)) / (10 ** this.precision);
      }
      return num;
    },
    resetInput() {
      this.inputNumber = null;
      this.validationMessage = '';
      this.$emit('reset');
    },
    increaseValue() {
      if (this.inputNumber == null || this.inputNumber === '') {
        this.inputNumber = 0;
      }
      this.validationMessage = '';
      if (this.inputNumber + this.step > this.max) {
        this.validationMessage = this.tooLargeMessage;
        this.$emit('error', this.tooLargeMessage, this.inputNumber + this.step);
      }
      this.inputNumber = Math.min(this.max, this.trimDecimalPlaces(this.inputNumber + this.step));
    },
    decreaseValue() {
      if (this.inputNumber == null || this.inputNumber === '') {
        this.inputNumber = 0;
      }
      this.validationMessage = '';
      if (this.inputNumber - this.step < this.min) {
        this.validationMessage = this.tooSmallMessage;
        this.$emit('error', this.tooSmallMessage, this.inputNumber - this.step);
      }
      this.inputNumber = Math.max(this.min, this.trimDecimalPlaces(this.inputNumber - this.step));
    },
    updateNumber() {
      if (this.inputNumber == null || this.inputNumber === '') {
        this.inputNumber = null;
        this.validationMessage = '';
        return;
      }
      if (this.inputNumber > this.max) {
        this.validationMessage = this.tooLargeMessage;
        this.$emit('error', this.tooLargeMessage, this.inputNumber);
        this.inputNumber = this.max;
      } else if (this.inputNumber < this.min) {
        this.validationMessage = this.tooSmallMessage;
        this.$emit('error', this.tooSmallMessage, this.inputNumber);
        this.inputNumber = this.min;
      } else if (this.nDecimalPlaces > this.precision) {
        this.validationMessage = this.precisionMessage;
        this.$emit('error', this.precisionMessage, this.inputNumber);
        this.inputNumber = this.trimDecimalPlaces(this.inputNumber);
      } else {
        this.validationMessage = '';
      }
    },
  },
};
</script>
